/**
 * @fileoverview Calendar Context
 *
 * Adapted from lramos33/big-calendar:
 *  - Host-controlled view (`view` + `onViewChange`) instead of route-
 *    based view switching (no Next.js routes here).
 *  - Host-provided events (`events` prop); no internal `localEvents`
 *    optimistic store — Convex `useQuery` handles refetches.
 *  - Adds an `onEventClick` callback so the ported month / week / day /
 *    agenda views can open Morgan's KeyDate side panel instead of the
 *    bundled `EventDetailsDialog`.
 *  - `users` defaults to an empty array; v1 of the Key Dates calendar
 *    runs in single-user mode (no per-practitioner filter).
 */

import { useState, useCallback, type ReactNode } from "react";

import type { IEvent, IUser } from "@/calendar/interfaces";
import type { CalendarDateMode } from "@/calendar/eventAdapter";
import type {
  TBadgeVariant,
  TCalendarView,
  TVisibleHours,
  TWorkingHours,
} from "@/calendar/types";

import { CalendarContext } from "./calendar-react-context";

const WORKING_HOURS: TWorkingHours = {
  0: { from: 0, to: 0 },
  1: { from: 8, to: 17 },
  2: { from: 8, to: 17 },
  3: { from: 8, to: 17 },
  4: { from: 8, to: 17 },
  5: { from: 8, to: 17 },
  6: { from: 8, to: 12 },
};

const VISIBLE_HOURS: TVisibleHours = { from: 7, to: 18 };

interface ProviderProps {
  children: ReactNode;
  view: TCalendarView;
  onViewChange: (view: TCalendarView) => void;
  events: IEvent[];
  users?: IUser[];
  onEventClick?: (event: IEvent) => void;
  onDateCellClick?: (date: Date, endDate?: Date) => void;
  initialDate?: Date;
  showJobName?: boolean;
}

export function CalendarProvider({
  children,
  view,
  onViewChange,
  events,
  users = [],
  onEventClick,
  onDateCellClick,
  initialDate,
  showJobName = false,
}: ProviderProps) {
  const [badgeVariant, setBadgeVariant] = useState<TBadgeVariant>("colored");
  const [visibleHours, setVisibleHours] =
    useState<TVisibleHours>(VISIBLE_HOURS);
  const [workingHours, setWorkingHours] =
    useState<TWorkingHours>(WORKING_HOURS);
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDate ?? new Date(),
  );
  const [selectedUserId, setSelectedUserId] = useState<IUser["id"] | "all">(
    "all",
  );
  const [dateMode, setDateMode] = useState<CalendarDateMode>("due");

  const handleSelectDate = useCallback((date: Date | undefined) => {
    if (!date) return;
    setSelectedDate(date);
  }, []);

  const handleEventClick = useCallback(
    (event: IEvent) => {
      onEventClick?.(event);
    },
    [onEventClick],
  );

  const handleDateCellClick = useCallback(
    (date: Date, endDate?: Date) => {
      onDateCellClick?.(date, endDate);
    },
    [onDateCellClick],
  );

  return (
    <CalendarContext.Provider
      value={{
        view,
        setView: onViewChange,
        selectedDate,
        setSelectedDate: handleSelectDate,
        selectedUserId,
        setSelectedUserId,
        badgeVariant,
        setBadgeVariant,
        users,
        visibleHours,
        setVisibleHours,
        workingHours,
        setWorkingHours,
        events,
        dateMode,
        setDateMode,
        onEventClick: handleEventClick,
        onDateCellClick: handleDateCellClick,
        showJobName,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
}
